import React, { useMemo } from "react"
import {
  Table,
  Tag,
  Space,
  Typography,
  message,
  Button,
  Dropdown,
} from "@mimber/ui-web"
import type { MenuProps, ColumnsType } from "@mimber/ui-web"
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
} from "@mimber/ui-web"
import { useRoles } from "@mimber/client/mimber-organization"
import type { OrganizationRoleWithRelationsDto } from "@mimber/schema"
import type { RolesTableProps } from "./RolesTable.types"

const { Text } = Typography

export const RolesTable: React.FunctionComponent<RolesTableProps> = ({
  onRowClick,
  onEdit,
  onDelete,
  className,
}) => {
  const { data: response, isLoading, error } = useRoles()

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load roles: ${error.message}`)
    }
  }, [error])

  const getActionMenuItems = (
    record: OrganizationRoleWithRelationsDto
  ): MenuProps["items"] => {
    const items: MenuProps["items"] = [
      {
        key: "edit",
        label: "Edit Role",
        icon: <EditOutlined />,
        onClick: () => onEdit?.(record),
      },
    ]

    // Only allow deleting non-system roles
    if (!record.isSystem) {
      items.push({
        key: "delete",
        label: "Delete Role",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => onDelete?.(record),
      })
    }

    return items
  }

  const columns: ColumnsType<OrganizationRoleWithRelationsDto> = useMemo(
    () => [
      {
        title: "Role Name",
        key: "name",
        render: (_, record) => (
          <Space>
            <Text strong>{record.name}</Text>
            {record.isTemplate && <Tag color="blue">Template</Tag>}
          </Space>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
        defaultSortOrder: "ascend" as const,
      },
      {
        title: "Description",
        key: "description",
        render: (_, record) => (
          <Text type="secondary">
            {record.description
              ? record.description.length > 60
                ? `${record.description.substring(0, 60)}...`
                : record.description
              : "—"}
          </Text>
        ),
      },
      {
        title: "Members",
        key: "memberAssignments",
        width: 100,
        render: (_, record) => {
          const count = record._count?.assignments ?? 0
          return (
            <Space>
              <UserOutlined />
              <Text>{count}</Text>
            </Space>
          )
        },
      },
      {
        title: "Groups",
        key: "groupAssignments",
        width: 100,
        render: (_, record) => {
          const count = record._count?.groupAssignments ?? 0
          return (
            <Space>
              <TeamOutlined />
              <Text>{count}</Text>
            </Space>
          )
        },
      },
      {
        title: "Max",
        key: "maxAssignments",
        width: 80,
        render: (_, record) => (
          <Text type="secondary">{record.maxAssignments ?? "∞"}</Text>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        render: (_, record) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{
                items: getActionMenuItems(record),
              }}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} size="small" />
            </Dropdown>
          </div>
        ),
      },
    ],
    [onEdit, onDelete]
  )

  return (
    <Table<OrganizationRoleWithRelationsDto>
      bordered
      columns={columns}
      dataSource={response?.data ?? []}
      loading={isLoading}
      rowKey="uuid"
      pagination={false}
      onRow={(record) => ({
        onClick: () => onRowClick?.(record),
        style: { cursor: onRowClick ? "pointer" : "default" },
      })}
      className={className}
    />
  )
}
